import {
  CLINIC_CRF_PACKAGE_V1,
  CLINIC_CRF_V1,
  CLINIC_CRF_V2,
  type ClinicCrfDefinitionV2,
  type ClinicCrfFieldPresentation,
  type ClinicCrfPackageForm,
  type ClinicCrfPackageV1,
  type ClinicCrfPresentation,
  type ClinicCrfPresentationSection,
  type ValidationIssue,
  type ValidationResult,
} from './types'
import { deepClone, isRecord, unicodeLength } from './runtime'
import { validateDefinition, validateDefinitionV2 } from './validation'

const FORM_CODE = /^[A-Z][A-Z0-9_-]{0,59}$/
const SECTION_ID = /^[a-z][a-z0-9_-]{0,79}$/
const SHA256 = /^[a-f0-9]{64}$/

function add(
  issues: ValidationIssue[],
  path: string,
  message: string,
  code: string,
  severity: 'error' | 'warning' = 'error',
): void {
  issues.push({ path, message, code, severity })
}

function rejectUnknown(
  value: Record<string, unknown>,
  allowed: readonly string[],
  path: string,
  issues: ValidationIssue[],
): void {
  for (const key of Object.keys(value)) {
    if (!allowed.includes(key)) add(issues, path ? `${path}.${key}` : key, 'Nieobsługiwany element pakietu.', 'package_unknown_key')
  }
}

export function defaultPresentation(definition: ClinicCrfDefinitionV2): ClinicCrfPresentation {
  return {
    default_locale: 'pl',
    sections: [
      {
        id: 'general',
        title: 'Formularz',
        field_paths: definition.fields.map((field) => field.path),
      },
    ],
    fields: {},
  }
}

export function createClinicCrfPackage(
  definition: ClinicCrfDefinitionV2,
  form: Partial<ClinicCrfPackageForm> = {},
  presentation: ClinicCrfPresentation = defaultPresentation(definition),
): ClinicCrfPackageV1 {
  return {
    format: CLINIC_CRF_PACKAGE_V1,
    form: {
      code: form.code ?? 'NEW_FORM',
      name: form.name ?? 'Nowy formularz kliniczny',
      version: form.version ?? 1,
      ...(form.description ? { description: form.description } : {}),
    },
    definition: deepClone(definition),
    presentation: deepClone(presentation),
    provenance: {
      generator: 'clinic-ecrf-maker',
      generator_version: '0.1.0',
      exported_at: new Date().toISOString(),
    },
  }
}

function validateForm(value: unknown, issues: ValidationIssue[]): value is ClinicCrfPackageForm {
  if (!isRecord(value)) {
    add(issues, 'form', 'Metadane formularza muszą być obiektem.', 'package_form')
    return false
  }
  rejectUnknown(value, ['code', 'name', 'version', 'description'], 'form', issues)
  let valid = true
  if (typeof value.code !== 'string' || !FORM_CODE.test(value.code)) {
    add(issues, 'form.code', 'Kod musi zaczynać się literą i zawierać wyłącznie A–Z, cyfry, _ lub -.', 'package_form_code')
    valid = false
  }
  if (typeof value.name !== 'string' || !value.name.trim() || unicodeLength(value.name) > 255) {
    add(issues, 'form.name', 'Nazwa jest wymagana i może mieć maksymalnie 255 znaków.', 'package_form_name')
    valid = false
  }
  if (!Number.isSafeInteger(value.version) || Number(value.version) < 1) {
    add(issues, 'form.version', 'Wersja musi być dodatnią liczbą całkowitą.', 'package_form_version')
    valid = false
  }
  if (value.description !== undefined && (typeof value.description !== 'string' || unicodeLength(value.description) > 4000)) {
    add(issues, 'form.description', 'Opis może mieć maksymalnie 4000 znaków.', 'package_form_description')
    valid = false
  }
  return valid
}

function validateFieldPresentation(
  value: unknown,
  path: string,
  options: string[] | undefined,
  issues: ValidationIssue[],
): value is ClinicCrfFieldPresentation {
  if (!isRecord(value)) {
    add(issues, path, 'Prezentacja pola musi być obiektem.', 'presentation_field')
    return false
  }
  rejectUnknown(value, ['help_text', 'placeholder', 'option_labels', 'width'], path, issues)
  let valid = true
  for (const key of ['help_text', 'placeholder'] as const) {
    if (value[key] !== undefined && (typeof value[key] !== 'string' || unicodeLength(value[key]) > 2000)) {
      add(issues, `${path}.${key}`, 'Tekst może mieć maksymalnie 2000 znaków.', 'presentation_text')
      valid = false
    }
  }
  if (value.width !== undefined && !['full', 'half'].includes(String(value.width))) {
    add(issues, `${path}.width`, 'Szerokość musi mieć wartość full albo half.', 'presentation_width')
    valid = false
  }
  if (value.option_labels !== undefined) {
    if (!isRecord(value.option_labels)) {
      add(issues, `${path}.option_labels`, 'Etykiety opcji muszą być mapą.', 'presentation_option_labels')
      valid = false
    } else {
      for (const [key, label] of Object.entries(value.option_labels)) {
        if (!options?.includes(key)) {
          add(issues, `${path}.option_labels.${key}`, 'Etykieta wskazuje opcję nieobecną w definicji.', 'presentation_option_unknown')
          valid = false
        }
        if (typeof label !== 'string' || !label.trim() || unicodeLength(label) > 500) {
          add(issues, `${path}.option_labels.${key}`, 'Etykieta opcji musi być niepustym tekstem do 500 znaków.', 'presentation_option_label')
          valid = false
        }
      }
    }
  }
  return valid
}

function validatePresentation(
  value: unknown,
  definition: ClinicCrfDefinitionV2,
  issues: ValidationIssue[],
): value is ClinicCrfPresentation {
  if (!isRecord(value)) {
    add(issues, 'presentation', 'Prezentacja musi być obiektem.', 'presentation_object')
    return false
  }
  rejectUnknown(value, ['default_locale', 'sections', 'fields'], 'presentation', issues)
  const paths = new Set(definition.fields.map((field) => field.path))
  let valid = true
  if (typeof value.default_locale !== 'string' || !/^[a-z]{2}(?:-[A-Z]{2})?$/.test(value.default_locale)) {
    add(issues, 'presentation.default_locale', 'Język musi mieć postać pl albo pl-PL.', 'presentation_locale')
    valid = false
  }
  const seenSectionIds = new Set<string>()
  const assignedPaths = new Set<string>()
  if (!Array.isArray(value.sections) || value.sections.length === 0 || value.sections.length > 100) {
    add(issues, 'presentation.sections', 'Prezentacja wymaga od 1 do 100 sekcji.', 'presentation_sections')
    valid = false
  } else {
    value.sections.forEach((section, index) => {
      const sectionPath = `presentation.sections.${index}`
      if (!isRecord(section)) {
        add(issues, sectionPath, 'Sekcja musi być obiektem.', 'presentation_section')
        valid = false
        return
      }
      rejectUnknown(section, ['id', 'title', 'description', 'field_paths'], sectionPath, issues)
      if (typeof section.id !== 'string' || !SECTION_ID.test(section.id)) {
        add(issues, `${sectionPath}.id`, 'Id sekcji ma nieprawidłowy format.', 'presentation_section_id')
        valid = false
      } else if (seenSectionIds.has(section.id)) {
        add(issues, `${sectionPath}.id`, 'Id sekcji występuje więcej niż raz.', 'presentation_section_unique')
        valid = false
      } else seenSectionIds.add(section.id)
      if (typeof section.title !== 'string' || !section.title.trim() || unicodeLength(section.title) > 500) {
        add(issues, `${sectionPath}.title`, 'Tytuł sekcji jest wymagany i może mieć maksymalnie 500 znaków.', 'presentation_section_title')
        valid = false
      }
      if (section.description !== undefined && (typeof section.description !== 'string' || unicodeLength(section.description) > 2000)) {
        add(issues, `${sectionPath}.description`, 'Opis sekcji może mieć maksymalnie 2000 znaków.', 'presentation_section_description')
        valid = false
      }
      if (!Array.isArray(section.field_paths)) {
        add(issues, `${sectionPath}.field_paths`, 'Sekcja wymaga listy ścieżek pól.', 'presentation_section_fields')
        valid = false
      } else {
        section.field_paths.forEach((fieldPath, fieldIndex) => {
          if (typeof fieldPath !== 'string' || !paths.has(fieldPath)) {
            add(issues, `${sectionPath}.field_paths.${fieldIndex}`, 'Sekcja wskazuje nieistniejące pole.', 'presentation_field_unknown')
            valid = false
          } else if (assignedPaths.has(fieldPath)) {
            add(issues, `${sectionPath}.field_paths.${fieldIndex}`, 'Pole może należeć tylko do jednej sekcji.', 'presentation_field_duplicate')
            valid = false
          } else assignedPaths.add(fieldPath)
        })
      }
    })
  }
  for (const fieldPath of paths) {
    if (!assignedPaths.has(fieldPath)) add(issues, 'presentation.sections', `Pole ${fieldPath} nie jest przypisane do sekcji.`, 'presentation_field_unassigned', 'warning')
  }
  if (value.fields !== undefined) {
    if (!isRecord(value.fields)) {
      add(issues, 'presentation.fields', 'Prezentacja pól musi być mapą.', 'presentation_fields')
      valid = false
    } else {
      for (const [fieldPath, presentation] of Object.entries(value.fields)) {
        const field = definition.fields.find((candidate) => candidate.path === fieldPath)
        if (!field) {
          add(issues, `presentation.fields.${fieldPath}`, 'Prezentacja wskazuje nieistniejące pole.', 'presentation_field_unknown')
          valid = false
          continue
        }
        if (!validateFieldPresentation(presentation, `presentation.fields.${fieldPath}`, field.options, issues)) valid = false
      }
    }
  }
  return valid
}

export function validatePackage(input: unknown): ValidationResult<ClinicCrfPackageV1> {
  const issues: ValidationIssue[] = []
  if (!isRecord(input)) {
    add(issues, '', 'Pakiet musi być obiektem JSON.', 'package_object')
    return { value: null, issues }
  }
  rejectUnknown(input, ['format', 'form', 'definition', 'presentation', 'provenance', 'integrity'], '', issues)
  if (input.format !== CLINIC_CRF_PACKAGE_V1) {
    add(issues, 'format', 'Obsługiwany jest wyłącznie clinic-crf-package.v1.', 'package_format')
  }
  validateForm(input.form, issues)
  const definitionResult = validateDefinition(input.definition)
  issues.push(...definitionResult.issues.map((candidate) => ({ ...candidate, path: `definition${candidate.path ? `.${candidate.path}` : ''}` })))
  if (definitionResult.value?.version !== CLINIC_CRF_V2) {
    add(issues, 'definition.version', 'Pakiet clinic-crf-package.v1 musi zawierać dokładny rdzeń clinic-crf.v2. Surowe v1 można zaimportować poza kopertą.', 'package_definition_version')
  }
  const definition = definitionResult.value?.version === CLINIC_CRF_V2 ? definitionResult.value : null
  if (definition && input.presentation !== undefined) validatePresentation(input.presentation, definition, issues)
  if (!isRecord(input.provenance)) {
    add(issues, 'provenance', 'Proweniencja musi być obiektem.', 'package_provenance')
  } else {
    rejectUnknown(input.provenance, ['generator', 'generator_version', 'exported_at'], 'provenance', issues)
    if (typeof input.provenance.generator !== 'string' || !input.provenance.generator.trim() || unicodeLength(input.provenance.generator) > 120) add(issues, 'provenance.generator', 'Nazwa generatora jest wymagana.', 'package_generator')
    if (typeof input.provenance.generator_version !== 'string' || !input.provenance.generator_version.trim() || unicodeLength(input.provenance.generator_version) > 60) add(issues, 'provenance.generator_version', 'Wersja generatora jest wymagana.', 'package_generator_version')
    if (typeof input.provenance.exported_at !== 'string' || unicodeLength(input.provenance.exported_at) > 80 || Number.isNaN(Date.parse(input.provenance.exported_at))) add(issues, 'provenance.exported_at', 'Czas eksportu musi być datą ISO 8601 do 80 znaków.', 'package_exported_at')
  }
  if (input.integrity !== undefined) {
    if (!isRecord(input.integrity)) add(issues, 'integrity', 'Integralność musi być obiektem.', 'package_integrity')
    else {
      rejectUnknown(input.integrity, ['definition_sha256'], 'integrity', issues)
      if (input.integrity.definition_sha256 !== undefined && (typeof input.integrity.definition_sha256 !== 'string' || !SHA256.test(input.integrity.definition_sha256))) {
        add(issues, 'integrity.definition_sha256', 'SHA-256 musi zawierać 64 małe znaki szesnastkowe.', 'package_hash')
      }
    }
  }
  if (issues.some((candidate) => candidate.severity === 'error') || !definition) return { value: null, issues }
  const normalized = deepClone(input) as unknown as ClinicCrfPackageV1
  normalized.definition = definition
  normalized.presentation ??= defaultPresentation(definition)
  return { value: normalized, issues }
}

export function parseClinicCrfDocument(source: string | unknown): ValidationResult<ClinicCrfPackageV1> {
  let input: unknown = source
  if (typeof source === 'string') {
    try {
      input = JSON.parse(source) as unknown
    } catch (error) {
      return {
        value: null,
        issues: [{ path: '', message: `Nieprawidłowy JSON: ${(error as Error).message}`, code: 'json_parse', severity: 'error' }],
      }
    }
  }
  if (isRecord(input) && input.format === CLINIC_CRF_PACKAGE_V1) return validatePackage(input)
  if (isRecord(input) && [CLINIC_CRF_V1, CLINIC_CRF_V2].includes(input.version as never)) {
    const definitionResult = validateDefinitionV2(input)
    if (!definitionResult.value) return { value: null, issues: definitionResult.issues }
    return {
      value: createClinicCrfPackage(definitionResult.value, {
        code: 'IMPORTED_FORM',
        name: 'Zaimportowany formularz',
        version: 1,
      }),
      issues: [
        ...definitionResult.issues,
        {
          path: '',
          message: 'Surowa definicja została umieszczona w pakiecie clinic-crf-package.v1.',
          code: 'wrapped_definition',
          severity: 'warning',
        },
      ],
      ...(definitionResult.migratedFrom ? { migratedFrom: definitionResult.migratedFrom } : {}),
    }
  }
  return {
    value: null,
    issues: [{ path: 'format', message: 'Plik nie jest pakietem clinic-crf ani definicją clinic-crf.v1/v2.', code: 'document_format', severity: 'error' }],
  }
}

type CanonicalIntegrityNode =
  | ['null']
  | ['boolean', boolean]
  | ['string', string]
  | ['number', string]
  | ['array', CanonicalIntegrityNode[]]
  | ['object', Array<[string, CanonicalIntegrityNode]>]

function float64Hex(value: number): string {
  const normalized = Object.is(value, -0) ? 0 : value
  const buffer = new ArrayBuffer(8)
  new DataView(buffer).setFloat64(0, normalized, false)
  return Array.from(new Uint8Array(buffer), (byte) => byte.toString(16).padStart(2, '0')).join('')
}

function canonicalIntegrityNode(value: unknown): CanonicalIntegrityNode {
  if (value === null) return ['null']
  if (typeof value === 'boolean') return ['boolean', value]
  if (typeof value === 'string') return ['string', value]
  if (typeof value === 'number') {
    if (!Number.isFinite(value)) throw new TypeError('Canonical integrity payload requires finite numbers.')
    if (Math.abs(value) > Number.MAX_SAFE_INTEGER) throw new TypeError('Canonical integrity payload number exceeds the interoperable safe range.')
    return ['number', float64Hex(value)]
  }
  if (Array.isArray(value)) return ['array', value.map((entry) => canonicalIntegrityNode(entry))]
  if (isRecord(value)) {
    return [
      'object',
      Object.keys(value)
        .sort()
        .map((key) => [key, canonicalIntegrityNode(value[key])] as [string, CanonicalIntegrityNode]),
    ]
  }
  throw new TypeError('Canonical integrity payload does not support undefined, functions or symbols.')
}

/**
 * Produces a tagged, deterministic integrity payload for the clinic-crf transport.
 *
 * Type tags preserve the distinction between arrays and objects (including empty
 * values), while numbers are represented by their big-endian IEEE-754 bits. That
 * makes the byte stream identical in JavaScript and PHP without depending on
 * runtime-specific decimal formatting.
 */
export function canonicalJson(value: unknown): string {
  return JSON.stringify(canonicalIntegrityNode(value))
}

export async function sha256Hex(value: string): Promise<string> {
  if (!globalThis.crypto?.subtle) throw new Error('Web Crypto SHA-256 is unavailable in this environment.')
  const digest = await globalThis.crypto.subtle.digest('SHA-256', new TextEncoder().encode(value))
  return Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, '0')).join('')
}

export async function definitionSha256(definition: ClinicCrfDefinitionV2): Promise<string> {
  return sha256Hex(canonicalJson(definition))
}

export async function packageWithIntegrity(packageValue: ClinicCrfPackageV1): Promise<ClinicCrfPackageV1> {
  const output = deepClone(packageValue)
  output.provenance.exported_at = new Date().toISOString()
  output.integrity = { definition_sha256: await definitionSha256(output.definition) }
  return output
}

export async function verifyPackageIntegrity(packageValue: ClinicCrfPackageV1): Promise<boolean | null> {
  if (!packageValue.integrity?.definition_sha256) return null
  return packageValue.integrity.definition_sha256 === (await definitionSha256(packageValue.definition))
}

export function prettyJson(value: unknown): string {
  return `${JSON.stringify(value, null, 2)}\n`
}

export function safeFilePart(value: string): string {
  const normalized = value.normalize('NFKD').replace(/[\u0300-\u036f]/g, '')
  return normalized.replace(/[^a-zA-Z0-9_-]+/g, '-').replace(/^-+|-+$/g, '').toLowerCase() || 'clinic-crf'
}

export function packageFileName(packageValue: ClinicCrfPackageV1): string {
  return `${safeFilePart(packageValue.form.code)}-v${packageValue.form.version}.clinic-crf.json`
}
